import { useEffect, useContext, useState, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { EventType, InteractionStatus } from "@azure/msal-browser";
import { useLocation } from "react-router-dom";
import { AppContext } from "./context/app.context";
import { ScheduleContext } from "./context/schedule.context";
import { ModelTsContext } from "./context/modelTS.context";
// ModelRwContext removed - Realm EOL Jan 13, 2026
import { ProjectListScreen } from "./screens/projectListScreen";
import { ScheduleScreen } from "./screens/scheduleScreen";
import { OptimizerScreen } from "./screens/optimizerScreen";
import { UserListScreen } from "./screens/userListScreen";
import { LogsScreen } from "./screens/logsScreen";
import { GlobalLoadingIndicator } from "./components/GlobalLoadingIndicator";
import "./App.css";
import Header from "./components/Header";
import JobsDetailModal from "./screens/schedule/JobsDetailModal";
import { createPortal } from "react-dom";
import CrewDetailModal from "./screens/schedule/CrewDetailModal";
import TimeMachineCrewModal from "./screens/schedule/TimeMachineCrewModal";
import TimeMachineAttributeHistoryModal from "./screens/schedule/TimeMachineAttributeHistoryModal";
import UnassignedDetailModal from "./screens/schedule/UnassignedDetailModal";
import ExtraCellAddModal from "./screens/schedule/ExtraCellAddModal";
import ExtraCellDetailModal from "./screens/schedule/ExtraCellDetailModal";
import JobSettingsModal from "./screens/schedule/JobSettingsModal";
import SuppressModal from "./screens/schedule/SuppressModal";
import JobInitModal from "./screens/schedule/JobInitModal";
import RepairOrderConfirmModal from "./screens/schedule/RepairOrderConfirmModal";
import WoWorkorderConfirmModal from "./screens/schedule/WoWorkorderConfirmModal";
import LockConfirmModal from "./screens/schedule/LockConfirmModal";
import LocationMetaModal from "./screens/schedule/LocationMetaModal";
import CrewMetaModal from "./screens/schedule/CrewMetaModal";
import UserDetailModal from "./screens/schedule/UserDetailModal";
import HelpModal from "./components/HelpModal";
import DashboardModal from "./components/DashboardModal";
import TimeMachineBanner from "./components/TimeMachineBanner";
import { TutorialOrchestrator } from "./components/TimeMachineTutorial";
import msLogo from "./assets/ms-logo.png";
// Import from /production to include DevTools in all environments (dev, UAT, prod)
// The standard import is tree-shaked out in production builds
import { ReactQueryDevtools } from "@tanstack/react-query-devtools/production";

//
//    npm start
//    npm run build:uat
//    aws s3 sync build/ s3://scheduler-uat.kellerinteriors.com
//    npm run build:production
//    aws s3 sync build/ s3://scheduler.kellerinteriors.com
//
//
// Microsoft 360 Azure Entra ActiveDirectory Login Authentication
// @azure/msal-react - github: https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/lib/msal-react
//                     docs: https://learn.microsoft.com/en-us/entra/identity-platform/scenario-spa-app-registration
// NOTE: MSAL config (clientId, authority) is in index.js
// NOTE: Client secret "Scheduler_JWT" is NOT in this React app - see index.js for details

const App = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [popupWindow, setPopupWindow] = useState(null);
  const {
    whichList,
    tabSwitching,
    setParameterLocation,
    setParameterWeek,
    isTimeTraveling,
    enterTimeTravel,
    openJobsDetailModal,
    openCrewDetailModal,
    openUnassignedDetailModal,
    openExtraCellAddModal,
    openExtraCellDetailModal,
    openJobSettingsModal,
    openSuppressModal,
    openJobInitModal,
    openRepairOrderConfirmModal,
    openWoWorkorderConfirmModal,
    openLockConfirmModal,
    openLocationMetaModal,
    openCrewMetaModal,
    openUserDetailModal,
    openHelpModal,
    openDashboardModal,
    openAttributeHistoryModal,
    setOpenAttributeHistoryModal,
    setOpenJobSettingsModal,
    setOpenUnassignedDetailModal,
    setOpenJobsDetailModal,
    setOpenCrewDetailModal,
    setOpenExtraCellAddModal,
    setOpenExtraCellDetailModal,
    setOpenSuppressModal,
    setOpenJobInitModal,
    setOpenRepairOrderConfirmModal,
    setOpenWoWorkorderConfirmModal,
    setOpenLockConfirmModal,
    setOpenLocationMetaModal,
    setOpenCrewMetaModal,
    setOpenUserDetailModal,
    setOpenHelpModal,
    setOpenDashboardModal,
  } = useContext(AppContext);
  // Use TanStack directly (Realm EOL Jan 13, 2026)
  const {
    mongoUser,
    userLoading,
    loginMsal,
    logoutUser,
    tsUserDocument,
    // Loading states for spinner
    locationsLoading,
    projectsLoading,
    candidatesLoading,
    attributesLoading,
    // Time Machine #114 Phase 4 — `timeTravelLoading` is consumed by the
    // banner directly via ModelTsContext, NOT used as a global-loader gate
    // here. Rationale: blocking the schedule grid behind a full-screen
    // loader hides ALL data until the slowest of the 4 historical queries
    // settles. Instead, each query lands its slot independently and
    // partial data appears progressively; the banner's inline
    // "Loading historical schedule…" hint signals the in-flight state.
  } = useContext(ModelTsContext);
  const { schedulePreparing } = useContext(ScheduleContext);

  // Access the URL string parameter labeled 'location' & 'week'
  // moved additional code to useEffect[locationObj] to remove render error
  const locationObj = useLocation();

  // - - - - - - - - - -

  const onLoginClick = () => {
    console.log("%cApp onLoginClick...", "color: #ADFF2F; font-weight: bold;");
    const callbackId = instance.addEventCallback((message) => {
      if (message.eventType === EventType.POPUP_OPENED) {
        // Save the popup window for focusing later
        setPopupWindow(message.payload.popupWindow);
      }
    });

    // If the popup window is already open, focus it
    if (popupWindow && !popupWindow.closed && popupWindow.focus) {
      popupWindow.focus();
    } else {
      const handleLoginError = (err) => {
        console.log(
          "%cApp onLoginClick handleLoginError (alert to user 'Login failed'): ",
          "color: #ADFF2F; font-weight: bold;",
          err
        );
        alert("Login failed!");
      };
      instance
        .loginPopup()
        .catch((err) => handleLoginError(err))
        .finally(() => {
          setPopupWindow(null);
          instance.removeEventCallback(callbackId);
        });
    }
  };

  // - - - - - - - - - - - - - - - - -  EFFECT HOOKS  - - - - - - - - - - - - - - - - - -
  //
  // locationObj                   -> process URL query parameters
  // inProgress                    -> clear URL query parameters
  // msal changes                  -> loginMsal (TanStack)
  //

  // Stabilize function references so effects don't re-run when queries refetch
  const loginMsalRef = useRef(loginMsal);
  useEffect(() => {
    loginMsalRef.current = loginMsal;
  }, [loginMsal]);

  const logoutUserRef = useRef(logoutUser);
  useEffect(() => {
    logoutUserRef.current = logoutUser;
  }, [logoutUser]);

  useEffect(() => {
    const params = new URLSearchParams(locationObj.search);
    const locationParam = params.get("location");
    const weekParam = params.get("week");
    setParameterLocation(locationParam);
    setParameterWeek(weekParam);

    console.log("%cApp URL Location parameter:", "color: #ADFF2F; font-weight: bold;", locationParam);
    console.log("%cApp URL Week parameter:", "color: #ADFF2F; font-weight: bold;", weekParam);

    // After you have parsed and used the parameters, clear them from the browser URL
    // window.history.replaceState({}, document.title, window.location.pathname);
    // 2025-08-20 - disabled this code here and moved it inside useEffect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationObj]);

  // - - - - - - - - - -
  // updateFilteredLists useEffect removed - Realm EOL Jan 13, 2026
  // All users now use TanStack data directly

  useEffect(() => {
    // 2025-08-20 - this useEffect solution was generated by Copilot AI GPT-4.1
    //              addresses error: "hash_empty_error: Hash value cannot be processed
    //                                because it is empty. Please verify that your redirectUri
    //                                is not clearing the hash"
    //              AI says: "This error occurs because the MSAL library expects a hash fragment
    //                        in the URL after authentication, but your code is clearing the URL
    //                        parameters and hash immediately after parsing them"

    // Only clear query params if there is no hash (MSAL has finished)
    if (!window.location.hash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [inProgress]);

  // - - - - - - - - - -

  useEffect(() => {
    const account = instance.getActiveAccount();

    console.log(
      "%cApp useEffect[instance, inProgress, mongoUser, accounts, loginMsal, logoutUser] inProgress: ",
      "color: #ADFF2F; font-weight: bold;",
      inProgress
    );
    const handleTokenError = (err) => {
      console.log(
        "%cApp useEffect[instance, inProgress, mongoUser, accounts, loginMsal, logoutUser] error (alert to user 'Login failed'): ",
        "color: #ADFF2F; font-weight: bold;",
        err
      );
      alert("Login failed!");
    };
    if (account && !mongoUser && inProgress === InteractionStatus.None) {
      instance
        .acquireTokenSilent({
          scopes: ["4ae8c1ac-41df-46bb-a881-011a10d1b09b/.default"],
          account: account,
        })
        .then((response) => {
          if (response) {
            // use stable ref to avoid effect dependency on the function.
            // #174 — pass the MSAL account too: the access token lacks the
            // profile name claims, so loginMsal sources the display + given/
            // family names from the account's ID-token claims (drives the
            // lock-attribution "Last, F").
            loginMsalRef.current(response.accessToken, response.account || account);
          }
        })
        .catch((err) => handleTokenError(err));
    } else if (!account) {
      logoutUserRef.current();
      // logoutUser();
    }
  }, [instance, inProgress, mongoUser, accounts]);
  // }, [instance, inProgress, mongoUser, accounts, loginMsal, logoutUser]);

  // - - - - - - - - - - - - - - - - -  APP RETURN  - - - - - - - - - - - - - - - - - -

  return (
    <div
      className="flex flex-col h-screen text-skin-primary bg-skin-base"
      style={
        // Time Machine #114 Phase 4.5 — override the app-root background to a
        // dark sepia while time-traveling. Bleeds through anywhere a child
        // doesn't have its own opaque bg (page margins, sidebar gutters,
        // beyond-grid edges) — reinforces the visual signal beyond just the
        // grid-area tint overlay. Works in both light and dark mode (this
        // overrides bg-skin-base which differs between modes).
        isTimeTraveling
          ? { backgroundColor: "#3e2c19" }
          : undefined
      }
    >
      {/*
        Time Machine #114 Phase 4 — when isTimeTraveling, the sepia banner
        REPLACES the Header entirely. This locks the user out of every
        Header affordance (company switch, tab buttons, Help, dark-mode,
        account menu, log-out) for the duration of the time-travel session.
        The only escape is the banner's own "Return to Live" button, which
        flips isTimeTraveling=false and re-renders the live Header.
      */}
      {isTimeTraveling ? (
        <TimeMachineBanner />
      ) : (
        <Header
          onOpenTimeMachinePicker={() => enterTimeTravel(new Date())}
        />
      )}
      {/* #124 Time Machine tutorial — intro modal, reset modal, and
          react-joyride driver. Renders once at the top level; its own
          state machine decides when to actually show anything. */}
      <TutorialOrchestrator />

      {!mongoUser ? (
        <div className="h-full bg-login-bg bg-center flex justify-center items-center bg-cover">
          {inProgress !== InteractionStatus.Startup &&
          inProgress !== InteractionStatus.HandleRedirect &&
          !userLoading ? (
            <div className="p-8 bg-skin-primary shadow-4 flex flex-col items-center gap-4">
              <span className="text-2xl font-normal">
                Welcome to the Jobs Scheduler
              </span>
              <div className="text-sm font-normal flex flex-col items-center">
                <div>Please use your Keller Interiors or SCG Surfaces</div>
                <div>account to log in</div>
              </div>
              <button
                className="flex items-center gap-2 p-2 shadow-4 rounded-lg bg-skin-primary-inverted"
                onClick={() => onLoginClick()}
              >
                <img src={msLogo} alt="Microsoft" />
                <span className="text-skin-primary-inverted text-sm font-bold">
                  Sign In with Microsoft
                </span>
              </button>
            </div>
          ) : (
            ""
          )}
        </div>
      ) : (
        <main className="main-container">
          {locationsLoading ||
          candidatesLoading ||
          projectsLoading ||
          attributesLoading ||
          schedulePreparing ||
          tabSwitching ? (
            <div className="loader"></div>
          ) : mongoUser ? (
            whichList === "jobList" ? (
              <ProjectListScreen></ProjectListScreen>
            ) : whichList === "logs" ? (
              <LogsScreen></LogsScreen>
            ) : whichList === "userList" ? (
              <UserListScreen></UserListScreen>
            ) : whichList === "optimizer" ? (
              <OptimizerScreen></OptimizerScreen>
            ) : (
              <ScheduleScreen></ScheduleScreen>
            )
          ) : (
            ""
          )}
        </main>
      )}

      {openJobsDetailModal.show
        ? createPortal(
            <JobsDetailModal
              jobsDetail={openJobsDetailModal.jobsDetail}
              setOpenJobsDetailModal={setOpenJobsDetailModal}
            ></JobsDetailModal>,
            document.body
          )
        : ""}
      {openUnassignedDetailModal.show
        ? createPortal(
            <UnassignedDetailModal
              setOpenUnassignedDetailModal={setOpenUnassignedDetailModal}
            ></UnassignedDetailModal>,
            document.body
          )
        : ""}
      {openExtraCellAddModal.show
        ? createPortal(
            <ExtraCellAddModal
              jobsDetail={openExtraCellAddModal.jobsDetail}
              setOpenExtraCellAddModal={setOpenExtraCellAddModal}
            ></ExtraCellAddModal>,
            document.body
          )
        : ""}
      {openExtraCellDetailModal.show
        ? createPortal(
            <ExtraCellDetailModal
              jobsDetail={openExtraCellDetailModal.jobsDetail}
              setOpenExtraCellDetailModal={setOpenExtraCellDetailModal}
            ></ExtraCellDetailModal>,
            document.body
          )
        : ""}
      {openCrewDetailModal.show
        ? createPortal(
            // Time Machine #114 Phase 5 — when time-traveling, render the
            // minimal forensic-history modal instead of the full live
            // CrewDetailModal (which has capacity/availability/defaults
            // tabs that aren't useful for forensic investigation).
            isTimeTraveling ? (
              <TimeMachineCrewModal
                crewDetail={openCrewDetailModal.crewDetail}
                setOpenCrewDetailModal={setOpenCrewDetailModal}
              />
            ) : (
              <CrewDetailModal
                crewDetail={openCrewDetailModal.crewDetail}
                setOpenCrewDetailModal={setOpenCrewDetailModal}
                weekDatesArray={openCrewDetailModal.weekDatesArray}
              ></CrewDetailModal>
            ),
            document.body
          )
        : ""}
      {openJobSettingsModal.show
        ? createPortal(
            <JobSettingsModal
              crewDetail={openJobSettingsModal.crewDetail}
              setOpenJobSettingsModal={setOpenJobSettingsModal}
            ></JobSettingsModal>,
            document.body
          )
        : ""}
      {openJobInitModal.show
        ? createPortal(
            <JobInitModal
              setOpenJobInitModal={setOpenJobInitModal}
            ></JobInitModal>,
            document.body
          )
        : ""}
      {openRepairOrderConfirmModal?.show
        ? createPortal(
            <RepairOrderConfirmModal
              setOpenRepairOrderConfirmModal={setOpenRepairOrderConfirmModal}
            ></RepairOrderConfirmModal>,
            document.body
          )
        : ""}
      {openWoWorkorderConfirmModal?.show
        ? createPortal(
            <WoWorkorderConfirmModal
              setOpenWoWorkorderConfirmModal={setOpenWoWorkorderConfirmModal}
            ></WoWorkorderConfirmModal>,
            document.body
          )
        : ""}
      {/* #174 — drop-on-locked-day confirmation */}
      {openLockConfirmModal?.show
        ? createPortal(
            <LockConfirmModal
              setOpenLockConfirmModal={setOpenLockConfirmModal}
            ></LockConfirmModal>,
            document.body
          )
        : ""}
      {openLocationMetaModal.show
        ? createPortal(
            <LocationMetaModal
              currentLocation={openLocationMetaModal.currentLocation}
              setOpenLocationMetaModal={setOpenLocationMetaModal}
            ></LocationMetaModal>,
            document.body
          )
        : ""}
      {openCrewMetaModal.show
        ? createPortal(
            <CrewMetaModal
              currentCrew={openCrewMetaModal.currentCrew}
              setOpenCrewMetaModal={setOpenCrewMetaModal}
            ></CrewMetaModal>,
            document.body
          )
        : ""}
      {openUserDetailModal.show
        ? createPortal(
            <UserDetailModal
              currentUser={openUserDetailModal.currentUser}
              setOpenUserDetailModal={setOpenUserDetailModal}
            ></UserDetailModal>,
            document.body
          )
        : ""}
      {openSuppressModal.show
        ? createPortal(
            <SuppressModal
              currentJob={openSuppressModal.currentJob}
              suppress={openSuppressModal.suppress}
              setOpenSuppressModal={setOpenSuppressModal}
            ></SuppressModal>,
            document.body
          )
        : ""}
      {openHelpModal.show
        ? createPortal(
            <HelpModal
              setOpenHelpModal={setOpenHelpModal}
            ></HelpModal>,
            document.body
          )
        : ""}
      {openDashboardModal.show
        ? createPortal(
            <DashboardModal
              setOpenDashboardModal={setOpenDashboardModal}
            ></DashboardModal>,
            document.body
          )
        : ""}

      {/* Time Machine #114 Phase 6 — attribute-cell history modal. Opened
          from the time-travel grid by clicking an attribute pseudo-job
          (JobTile attribute branch). Renders only when explicitly opened
          and only during time-travel (the click handler enforces that). */}
      {openAttributeHistoryModal && openAttributeHistoryModal.show
        ? createPortal(
            <TimeMachineAttributeHistoryModal
              payrollNo={openAttributeHistoryModal.payrollNo}
              date={openAttributeHistoryModal.date}
              setOpenAttributeHistoryModal={setOpenAttributeHistoryModal}
            />,
            document.body
          )
        : ""}

      {/* Global Loading Indicator - visible across all screens and behind modals */}
      <GlobalLoadingIndicator />

      {/* TanStack Query DevTools - only shown if user's devTools flag is true */}
      {tsUserDocument?.devTools && <ReactQueryDevtools initialIsOpen={false} />}
    </div>
  );
};

export default App;
